// Pure helpers for the charge Controls section (Principal Term + Concurrency).
// CommonJS so it can be unit-tested with node:test (tests/chargeControlHelpers.test.js)
// and imported by both CDBWorkspace.vue (UI) and sharedComponent.vue (calc).

// isPrincipal values on newExposureObject: 1 = determinate principal, 6 = STIR
// principal. The principal term always behaves as Consecutive.
const PRINCIPAL_ROLES = [1, 6];

// The Principal Term dropdown is only changeable when this charge is tied with
// others for the greatest term (the user can pick which is principal). With a
// single greatest term (or a subordinate charge) it is shown but disabled.
function isPrincipalTermChangeable(charge, principalTies) {
    return (
        Array.isArray(principalTies) &&
        principalTies.length > 1 &&
        principalTies.includes(charge?.CountNumber)
    );
}

// The Concurrency control is locked to Consecutive when the charge must be consecutive by
// law (IsMandatoryConsecutive), or when it is the determinate principal term (isPrincipal
// 1), which always runs consecutive. A 667.6 charge (isPrincipal 6) is NOT auto-locked:
// its consecutive/concurrent choice is left to the user unless it is mandatory consecutive
// (667.6(d)) — a non-mandatory 667.6 charge (667.6(c)) may be run concurrent.
function isConcurrencyLocked(charge) {
    return (
        !!charge?.IsMandatoryConsecutive ||
        charge?.newExposureObject?.isPrincipal == 1
    );
}

// The value written to newExposureObject.concurrent during calculation. Only the
// principal (isPrincipal 1/6) and mandatory-consecutive charges are forced
// consecutive (false); everyone else uses their stored Concurrency. Never mutates
// charge.Concurrency, so the user's selection is retained for when the charge is no
// longer principal. The principal is the anchor (identified by getPrincipalCharge,
// which honors the user's Preferred pick), so there is no separate "biggest charge".
function effectiveConcurrent(charge) {
    if (isConcurrencyLocked(charge)) return false;
    return charge?.Concurrency;
}

// Comparator for choosing the principal among tied charges. Order:
//   1. determinate (exposure) desc
//   2. user Preferred (IsPrincipal) — wins even if the charge is concurrent, because
//      choosing a charge as principal is exactly what forces it consecutive
//   3. Concurrency — consecutive (0/false) before concurrent (1/true)
//   4. countNumber asc
// Each candidate is { determinate, IsPrincipal, Concurrency, countNumber }.
function comparePrincipalCandidates(a, b) {
    if (b.determinate !== a.determinate) return b.determinate - a.determinate;
    const aPref = a.IsPrincipal == true ? 1 : 0;
    const bPref = b.IsPrincipal == true ? 1 : 0;
    if (aPref !== bPref) return bPref - aPref;
    const aConc = a.Concurrency == true ? 1 : 0;
    const bConc = b.Concurrency == true ? 1 : 0;
    if (aConc !== bConc) return aConc - bConc;
    return a.countNumber - b.countNumber;
}

// Conduct enhancements (6) and special allegations (2) attach to a base charge (5)
// that shares their CountNumber. The Mustafaa rule stays such an enhancement when its
// concurrency differs from its parent's, so a changeable enhancement should follow the
// base charge's concurrency.
const ENHANCEMENT_ROLES = [2, 6];

// Auto-mirror each changeable enhancement's concurrency to its base charge, but ONLY
// when the base charge's (effective) concurrency actually CHANGES during the session —
// so a user's saved/manual enhancement selection is preserved on load and whenever the
// base is untouched. Runs once per calc, AFTER handleConcurrency has set every charge's
// newExposureObject.concurrent. Mutates charges in place and returns the list of
// children whose stored Concurrency changed, so the caller can persist them.
//
// Baseline tracking: base._lastSyncedConcurrency is seeded (without touching children)
// the first time a base charge is seen. Charges come fresh from the backend on load, so
// an undefined baseline reliably means "just loaded" — children keep their saved state.
// A later divergence (direct toggle or an indirect principal shift) triggers the mirror.
function syncEnhancementConcurrency(charges) {
    const changed = [];
    if (!Array.isArray(charges)) return changed;

    charges.forEach((base) => {
        if (base?.CrimeTypeId !== 5) return;
        const effective = !!base.newExposureObject?.concurrent;

        if (base._lastSyncedConcurrency === undefined) {
            base._lastSyncedConcurrency = effective; // seed on load — do not override children
            return;
        }
        if (base._lastSyncedConcurrency === effective) return; // no change — preserve overrides
        base._lastSyncedConcurrency = effective;

        charges.forEach((child) => {
            if (child === base || child?.CountNumber !== base.CountNumber) return;
            if (!ENHANCEMENT_ROLES.includes(child?.CrimeTypeId)) return;
            // Mandatory-consecutive (or principal) enhancements can't mirror — leave them
            // to be stayed on mismatch, exactly as before.
            if (isConcurrencyLocked(child)) return;

            const target = effective ? 1 : 0;
            if (child.Concurrency != target) {
                child.Concurrency = target;
                if (child.newExposureObject) child.newExposureObject.concurrent = effective;
                changed.push({ Id: child.Id, CaseId: child.CaseId, Concurrency: target });
            }
        });
    });

    return changed;
}

// Whether a charge is running under the PC 667.6 full-term subordinate scheme, in which
// case it is excluded from principal candidacy (classified isPrincipal 6: full term, no
// P/S label) rather than competing for the principal on exposure. The live determinant is
// the per-charge RunSTIRCalculation toggle (true = 667.6, false = PC 1170.1(a)) — so
// switching a charge back to 1170.1 makes it a normal principal candidate again. The
// crime must also be eligible for the scheme (IncludeInPrincipalCalculation == 0, a
// crime-definition attribute from VCharges2). Note the correct field spelling
// RunSTIRCalculation — the original inline check had a typo (RunSTIRCalculcation) that
// left this dormant.
// A single-charge case has fewer than two convicted base charges. The 667.6 subordinate
// scheme is moot there, so such a case defaults to PC 1170.1(a). (An attached enhancement
// is not a second charge; an acquitted/dismissed base charge doesn't count.)
function isSingleChargeCase(charges) {
    if (!Array.isArray(charges)) return false;
    return charges.filter((c) => c?.CrimeTypeId == 5 && c?.ChargeStatus == 0).length < 2;
}

function runsUnder667Scheme(charge, charges) {
    if (isSingleChargeCase(charges)) return false;
    // Attempt and Conspiracy charges are always sentenced under PC 1170.1(a); they never
    // fall under the 667.6 scheme, regardless of the RunSTIRCalculation toggle.
    if (charge?.CrimeState === 'Attempt' || charge?.CrimeState === 'Conspiracy') return false;
    return (
        charge?.RunSTIRCalculation == true &&
        charge?.IncludeInPrincipalCalculation == 0
    );
}

// The effective subordinate STIR factor for a charge. Attempt and Conspiracy charges are
// sentenced as normal crimes — 1/3 of the middle term — never under the 667.6 full-term
// scheme, so their factor is always 3 regardless of the crime definition's
// SubordinateSTIRFactor (which reflects the completed 667.6 offense). Any other charge keeps
// its own factor. Applied (as a normalization) in setExposureObject so the whole app — the
// Sentencing Scheme display and the subordinate STIR calc — treats these as normal crimes.
function effectiveSubordinateStirFactor(charge) {
    if (charge?.CrimeState === 'Attempt' || charge?.CrimeState === 'Conspiracy') return 3;
    return charge?.SubordinateSTIRFactor;
}

// The default value for a new charge's RunSTIRCalculation flag (true = default to the PC
// 667.6 scheme, false = default to PC 1170.1(a)). Only a completed crime defaults to 667.6;
// an Attempt or Conspiracy defaults to PC 1170.1(a). Any other/absent CrimeState keeps the
// completed default (true). This is only the DEFAULT — the user can still toggle the scheme
// on the charge afterward.
function defaultRunStir(crimeState) {
    return crimeState !== 'Attempt' && crimeState !== 'Conspiracy';
}

// The "Sentencing Scheme" label shown for a charge. For a charge running under the PC
// 667.6 scheme (its CrimeCategorySectionNumber references 667.6), the label reflects the
// charge's concurrency: a concurrent charge shows "667.6(c)" (the discretionary
// subdivision), a consecutive charge shows "667.6(c) or (d)". Any other scheme shows its
// raw CrimeCategorySectionNumber unchanged.
function sentencingSchemeLabel(charge) {
    const code = charge?.CrimeCategorySectionNumber;
    if (!String(code ?? '').includes('667.6')) return code;
    return charge?.newExposureObject?.concurrent ? 'PC 667.6(c)' : 'PC 667.6(c) or (d)';
}

// Whether a charge row is running under the 667.6 scheme for the Principal-term column:
// a base charge classified isPrincipal 6, or a conduct enhancement whose base charge is
// (parentCharge). Such rows show "N/A" for the principal term rather than a checkmark or
// blank. A single-charge 667.6 case is isPrincipal 1 (defaults to 1170.1), so it is the
// principal and correctly shows the checkmark, not N/A.
function usesSixSevenSixScheme(charge) {
    return (
        charge?.newExposureObject?.isPrincipal == 6 ||
        charge?.parentCharge?.newExposureObject?.isPrincipal == 6
    );
}

// Whether a conduct enhancement (CrimeTypeId 6) is attached to a base charge running the
// 667.6 scheme — i.e. a base charge (CrimeTypeId 5) at the same CountNumber classified
// isPrincipal 6. sentencing() uses this to route the enhancement into the 667.6 report
// bucket. It looks up the base by count rather than the enhancement's own isPrincipal
// because a 667.6 enhancement can be classified isPrincipal 6 (SubordinateSTIRTerm == 3)
// OR isPrincipal 2 (otherwise), and the latter is indistinguishable from an
// indeterminate-charge enhancement without checking the base.
function isSixSevenSixEnhancement(charge, charges) {
    if (charge?.CrimeTypeId != 6 || !Array.isArray(charges)) return false;
    return charges.some(
        (base) =>
            base?.CrimeTypeId == 5 &&
            base?.CountNumber == charge?.CountNumber &&
            base?.newExposureObject?.isPrincipal == 6
    );
}

// The determinate sentence served is the longer of the consecutive block (principal +
// consecutive subordinates + enhancements) and the longest concurrent charge — a
// concurrent charge extends the sentence only when it runs past the consecutive total.
// (Previously the total ignored maxConcurrentTerm, assuming concurrent < consecutive.)
function combineDeterminateTotal(consecutiveTotal, maxConcurrentTerm) {
    return Math.max(consecutiveTotal || 0, maxConcurrentTerm || 0);
}

// Determinate term for a conduct enhancement attached to a 667.6 (isPrincipal 6) base,
// under the STIR scheme (mirrors the subordinate-base STIR math). The base's
// EnhancementSTIRTerm/Factor drive it: EnhancementSTIRTerm == 3 means "use the user's
// selected term" (any other value is a fixed 1-based term); EnhancementSTIRFactor is the
// divisor (1 = full term, 3 = 1/3), falling back to the enhancement's own factor, then to
// 1 (full). The triad is indexed 1-based (triad[term - 1]) like every other sentencing
// branch — the original inline code dropped the -1, reading triad[SelectedTerm] and
// producing NaN for an upper-term (SelectedTerm 3) charge, which broke the whole total.
function stirEnhancementDeterminate(parentCharge, charge) {
    const triad = charge?.newExposureObject?.triad;
    if (!Array.isArray(triad)) return charge?.newExposureObject?.determinate;
    const enhTerm = parentCharge?.EnhancementSTIRTerm;
    const term = (enhTerm == 3 ? charge?.SelectedTerm : enhTerm) || 1; // 1-based
    let factor = parentCharge?.EnhancementSTIRFactor;
    if (factor == null || factor === 3) factor = charge?.EnhancementSTIRFactor;
    if (!(factor > 0)) factor = 1; // guard: no valid reducing factor -> full term
    return (1 / factor) * triad[term - 1];
}

// Display label for the PC 1170(h) Eligibility info-screen cell. A serious or violent
// felony is not 1170(h) eligible, so any charge that is serious/violent — natively
// (IsSerious/IsViolent) or made so by an enhancement (EnhancedSerious/EnhancedViolent) —
// reads "No", overriding its stored eligibility (which does NOT itself reflect
// serious/violent, e.g. PC 245.5(a) stores Yes). It reads "No (Enhanced)" when an
// enhancement is what made it serious/violent (the base itself was not); a natively
// serious/violent charge is a plain "No". Otherwise the crime's stored eligibility is
// shown (3=Yes, 2=Maybe, 1=No, 0=N/A).
function pc1170hDisplay(charge) {
    const nativelySeriousOrViolent = !!(charge?.IsSerious || charge?.IsViolent);
    const seriousOrViolent = nativelySeriousOrViolent || !!(charge?.EnhancedSerious || charge?.EnhancedViolent);
    if (seriousOrViolent) {
        // Already serious/violent by definition => plain "No"; only "(Enhanced)" when an
        // enhancement is what first made it serious/violent (it was neither natively).
        return nativelySeriousOrViolent ? 'No' : 'No (Enhanced)';
    }
    const e = charge?.PC1170HEligibility;
    if (e == 3) return 'Yes';
    if (e == 2) return 'Maybe';
    if (e == 1) return 'No';
    if (e == 0) return 'N/A';
    return '';
}

// Per-category config for the info-screen authority-text buttons:
//   enhanced(charge) — is this category's value changed by an enhancement (base was not so)?
//   match(enh)       — does this enhancement carry the overriding value?
//   text             — the *Text field holding the authority text (on base and enhancement).
const AUTHORITY_CATEGORIES = {
    serious: { enhanced: (c) => !!c?.EnhancedSerious && !c?.IsSerious, match: (e) => !!e?.IsSerious, text: 'SeriousText' },
    violent: { enhanced: (c) => !!c?.EnhancedViolent && !c?.IsViolent, match: (e) => !!e?.IsViolent, text: 'ViolentText' },
    strike: {
        enhanced: (c) => (!!c?.EnhancedSerious || !!c?.EnhancedViolent) && !(c?.IsSerious || c?.IsViolent),
        match: (e) => !!e?.IsSerious || !!e?.IsViolent,
        text: 'StrikeText',
    },
    superStrike: { enhanced: (c) => !!c?.EnhancedSuperStrike && !c?.IsSuperStrike, match: (e) => !!e?.IsSuperStrike, text: 'SuperStrikeText' },
    thirdStrike: { enhanced: (c) => !!c?.EnhancedThirdStrikeTrigger && !c?.IsThirdStrikeTrigger, match: (e) => !!e?.IsThirdStrikeTrigger, text: 'ThirdStrikeTriggerText' },
    // 1170(h) is removed because the charge became (enhancement-induced) serious/violent.
    pc1170h: {
        enhanced: (c) => !(c?.IsSerious || c?.IsViolent) && (!!c?.EnhancedSerious || !!c?.EnhancedViolent),
        match: (e) => !!e?.IsSerious || !!e?.IsViolent,
        text: 'PC1170HText',
    },
};

// An overriding enhancement still contributes its elevated status (and thus its authority
// text) when it is convicted OR stayed — a stayed enhancement keeps its status applied to
// the base charge. Only acquitted (1) and dismissed (2) enhancements drop out.
// ChargeStatus: 0=Convicted, 1=Acquitted, 2=Dismissed, 3=Stayed 654, 4=Stayed, 6=Stayed.
const AUTHORITY_ENHANCEMENT_STATUSES = [0, 3, 4, 6];

// The authority-text popup body for an info-screen category button. When the charge's
// value in that category was changed by an enhancement, show the OVERRIDING enhancement's
// text for that category (found among the charge's conduct/special-allegation enhancements
// of the same count) instead of the base charge's text (which describes the un-enhanced
// value). Falls back to the base charge's text when the value isn't enhanced, no overriding
// enhancement is found, or that enhancement has no text.
function enhancedAuthorityText(charge, charges, category) {
    const cfg = AUTHORITY_CATEGORIES[category];
    const baseText = cfg ? charge?.[cfg.text] : undefined;
    if (!cfg || !cfg.enhanced(charge)) return baseText;
    if (Array.isArray(charges)) {
        const enh = charges.find(
            (e) =>
                e && e !== charge &&
                e.CountNumber == charge?.CountNumber &&
                (e.CrimeTypeId == 6 || e.CrimeTypeId == 2) &&
                AUTHORITY_ENHANCEMENT_STATUSES.some((s) => e.ChargeStatus == s) &&
                cfg.match(e)
        );
        if (enh && enh[cfg.text] != null && enh[cfg.text] !== '') return enh[cfg.text];
    }
    return baseText; // fallback to the base term's text (even if it reflects the un-enhanced value)
}

module.exports = {
    isPrincipalTermChangeable,
    isConcurrencyLocked,
    effectiveConcurrent,
    comparePrincipalCandidates,
    syncEnhancementConcurrency,
    runsUnder667Scheme,
    defaultRunStir,
    effectiveSubordinateStirFactor,
    isSingleChargeCase,
    usesSixSevenSixScheme,
    isSixSevenSixEnhancement,
    sentencingSchemeLabel,
    combineDeterminateTotal,
    stirEnhancementDeterminate,
    pc1170hDisplay,
    enhancedAuthorityText,
    PRINCIPAL_ROLES,
    ENHANCEMENT_ROLES,
};
